import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import BasicFetchError from "./BasicFetchError";
import DetailsWrapper from "./DetailsWrapper";
import useContactDetails, { ContactTypes } from "../hooks/useContactDetails";
import { Dropdown } from "react-bootstrap";
import { IconButton } from "../../../components/buttons";
import { FormCheck, FormInput, FormSelect } from "../../../components/forms";
import { StandardTable } from "../../../components/tables";
import { StandardTableCreatingRow } from "../../../components/tables/StandardTable";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";

const ActiveColumn = styled.td`
    width: 70px;

    && > div.row {
        justify-content: center;
    }

    && > div.row > div {
        margin: 0;
    }
`;

const DefaultColumn = styled.td`
    width: 78px;

    && > div.row {
        justify-content: center;
    }

    && > div.row > div {
        margin: 0;
    }
`;

const ContactDetailsTableEmptyRow = ({ columns, onCreatePhoneClicked, onCreateEmailClicked, ...rest }) => {
    return <tr {...rest}>
        <td colSpan={columns.length}>
            There are no contacts available for this account.
            You can click <button className="anchor" onClick={onCreatePhoneClicked}>here</button> to create a phone number, or <button className="anchor" onClick={onCreateEmailClicked}>here</button> to create an email.
        </td>
    </tr>
};

const ContactDetailsTableLoadingRow = ({ columns, loadingLines, ...rest }) => {
    return [...Array(loadingLines)].map((_, i) => (
        <tr key={`loading-${i}`} {...rest}>
            <td colSpan={columns.length}>
                <Skeleton count={1} height={37} />
            </td>
        </tr>
    ));
};

const ContactDetailsTableRow = ({ allowDelete = true, columns, data, onDefaultChanged, onDeleteClicked, onFieldChanged, ...rest }) => {
    if (data == null) {
        // Creating row
        return <StandardTableCreatingRow columns={columns} {...rest} />;
    }

    const { contactDetailId, description, details, isActiveDetails, isDefault, type, typeOptions, superType } = data;

    if (!contactDetailId) {
        // Type header
        return <tr key={`${type}-header`} {...rest}>
            <td className="pt-2 pb-0 opacity-75" colSpan={columns.length}>
                <strong>{type}</strong>
            </td>
        </tr>
    }

    return <tr key={contactDetailId} {...rest}>
        <td className="col-2">
            <FormSelect
                defaultValue={type}
                options={typeOptions}
                onBlur={(values) => onFieldChanged(contactDetailId, 'type', values.value)}
            />
        </td>
        <td>
            <FormInput
                value={details}
                placeholder="Contact details"
                onBlur={(value) => onFieldChanged(contactDetailId, 'details', value)}
            />
        </td>
        <td>
            <FormInput
                value={description}
                placeholder="Description"
                onBlur={(value) => onFieldChanged(contactDetailId, 'description', value)}
            />
        </td>
        <ActiveColumn className="align-middle text-center">
            <FormCheck
                type="checkbox"
                isChecked={isActiveDetails}
                onChange={() => onFieldChanged(contactDetailId, 'isActiveDetails', !isActiveDetails)}
            />
        </ActiveColumn>
        <DefaultColumn className="align-middle text-center">
            <FormCheck
                type="radio"
                name={`isContactDetail${superType}DefaultRadio`}
                isChecked={isDefault}
                onChange={() => onDefaultChanged(contactDetailId)}
            />
        </DefaultColumn>
        {allowDelete === true && (
            <td className="col-md-auto">
                <IconButton icon="fa-trash-can" variant="danger" onClick={() => onDeleteClicked(data)} />
            </td>
        )}
    </tr>
};

const getDeleteWarningMessage = (description) => {
    if (!description) {
        return `Are you sure you want to delete these contact details?`;
    }

    return `Are you sure you want to delete the contact details "${description}"?`;
};

const ContactDetails = ({ masterAccountId, loadingLines = 2 }) => {
    const {
        createContactDetails,
        tableRows,
        deleteContactDetails,
        error,
        isError,
        isLoading,
        reload,
        setDefaultContactDetails,
        updateContactDetails
    } = useContactDetails(masterAccountId);

    const onCreateContactDetailsPressed = (e) => createContactDetails(e);

    const onDefaultContactDetailsChanged = (contactDetailId) => setDefaultContactDetails(contactDetailId);

    const onDeleteContactDetailsPressed = (contactDetail) => {
        const { contactDetailId, description } = contactDetail;
        openConfirmModal({
            dialogTitle: "Delete Contact Details?",
            message: getDeleteWarningMessage(description),
            confirmButtonText: "Delete",
            confirmButtonVariant: 'danger'
        }).then(
            (result) => {
                if (result === true) {
                    deleteContactDetails(contactDetailId)
                }
            },
            () => {}
        );
    }

    const onFieldChanged = (contactDetailId, property, value) => updateContactDetails(contactDetailId, property, value);

    if (error || isError === true) {
        return <BasicFetchError error={error} retryCallback={reload} />
    }

    return <DetailsWrapper label="Contact Details" collapsedByDefault={true}>
        <StandardTable
            className="table-borderless"
            columns={[
                { label: 'Contact Type', property: 'title' },
                { label: 'Details', property: 'forenames' },
                { label: 'Display Name', property: 'surname' },
                {
                    label: 'Active',
                    property: 'isActiveDetails',
                    headerClassName: 'text-center'
                },
                {
                    label: 'Default',
                    property: 'isDefault',
                    headerClassName: 'text-center'
                },
                {
                    label: '',
                    property: '',
                    headerRenderer: (props) => (
                        <td key={props.key} style={{ width: '32px' }}>
                            <Dropdown className="caret-off">
                                <Dropdown.Toggle id="create-dropdown" as={IconButton} icon="fa-plus" variant="success" />
                                <Dropdown.Menu>
                                    <Dropdown.Header>
                                        Create New Contact Details
                                    </Dropdown.Header>
                                    {
                                        Object.keys(ContactTypes).map((key, i) => (
                                            <Dropdown.Item key={i} onClick={() => onCreateContactDetailsPressed(key)}>
                                                {key}
                                            </Dropdown.Item>
                                        ))
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                    )
                }
            ]}
            data={tableRows}
            error={error}
            // isCreating={isCreating}
            isLoading={isLoading}
            EmptyRowComponent={ContactDetailsTableEmptyRow}
            emptyRowComponentProps={{
                onCreatePhoneClicked: () => onCreateContactDetailsPressed(Object.keys(ContactTypes)[0]),
                onCreateEmailClicked: () => onCreateContactDetailsPressed(Object.keys(ContactTypes)[1])
            }}
            hideHeaderOnEmpty={true}
            LoadingRowComponent={ContactDetailsTableLoadingRow}
            loadingRowComponentProps={{ loadingLines }}
            RowComponent={ContactDetailsTableRow}
            rowComponentProps={{
                allowDelete: true,
                onDefaultChanged: onDefaultContactDetailsChanged,
                onDeleteClicked: onDeleteContactDetailsPressed,
                onFieldChanged
            }}
        />
    </DetailsWrapper>
};

export default ContactDetails;